// ============================================
// EventBus - Sistema de eventos desacoplado
// ============================================

type EventCallback<T = unknown> = (data: T) => void;

interface EventMap {
  // Game events
  'game:pause': void;
  'game:resume': void;
  'game:start': void;
  'game:over': void;

  // Player events
  'player:move': { x: number; y: number; z: number };
  'player:interact': string;
  'player:healthChange': number;

  // Interaction events
  'interaction:available': { id: string; label: string };
  'interaction:unavailable': void;
  'interaction:triggered': string;

  // Audio events
  'audio:play': { id: string; category: string };
  'audio:stop': string;
  'audio:volumeChange': { id: string; volume: number };

  // Scene events
  'scene:load': string;
  'scene:unload': string;
  'scene:transition': { from: string; to: string };

  // Horror events
  'horror:event': { type: string; intensity: number };
  'horror:lightingChange': { intensity: number; color: string };

  // Puzzle events
  'puzzle:solved': string;
  'puzzle:failed': string;
  'puzzle:hint': { puzzleId: string; hint: string };

  // Save events
  'save:load': void;
  'save:write': void;
  'save:error': string;

  // Radio events
  'radio:tune': number;
  'radio:transmission': { frequency: number; message: string };
  'radio:static': number;
}

export class EventBus {
  private static instance: EventBus;
  private listeners: Map<string, Set<EventCallback<unknown>>> = new Map();

  private constructor() {}

  static getInstance(): EventBus {
    if (!EventBus.instance) {
      EventBus.instance = new EventBus();
    }
    return EventBus.instance;
  }

  on<K extends keyof EventMap>(event: K, callback: EventCallback<EventMap[K]>): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback as EventCallback<unknown>);

    // Return unsubscribe function
    return () => {
      this.listeners.get(event)?.delete(callback as EventCallback<unknown>);
    };
  }

  off<K extends keyof EventMap>(event: K, callback: EventCallback<EventMap[K]>): void {
    this.listeners.get(event)?.delete(callback as EventCallback<unknown>);
  }

  emit<K extends keyof EventMap>(event: K, data: EventMap[K]): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error(`[EventBus] Error in event handler for "${event}":`, error);
        }
      });
    }
  }

  clear(): void {
    this.listeners.clear();
  }
}

export const eventBus = EventBus.getInstance();
